// Chrome shared by the public marketing pages (landing, changelog): top bar,
// footer, section eyebrow, theme-aware app screenshots and the mobile
// "Get the app" bar.
import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { Wordmark } from './Wordmark';
import { ThemeToggle } from './ThemeToggle';
import { APP_STORE_URL, PLAY_STORE_URL } from './StoreBadges';
import { getTheme, type Theme } from '../lib/theme';
import icLogo from '../assets/insert-coin-logo.gif';

/** The site's current theme, following the toggle (it flips `data-theme` on
 *  <html>), so app screenshots can match it. */
export function useSiteTheme(): Theme {
  const [theme, setTheme] = useState<Theme>('light');
  useEffect(() => {
    setTheme(getTheme());
    const obs = new MutationObserver(() => setTheme(getTheme()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);
  return theme;
}

// Section anchors on the landing page, plus the changelog.
const NAV: [string, string][] = [
  ['Features', '#features'],
  ['Screens', '#screens'],
  ['Pricing', '#pricing'],
  ['Changelog', '/changelog'],
];

export function TopBar() {
  // On the landing page a bare "#id" link is left to the browser (the router
  // ignores those), so it scrolls natively. Elsewhere it routes to "/#id" and
  // the landing page scrolls there once mounted — see `useScrollToHash`.
  const { path } = useLocation();
  const hrefFor = (href: string) =>
    href.startsWith('#') && path !== '/' ? `/${href}` : href;
  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        background: 'color-mix(in srgb, var(--cream-2) 70%, transparent)',
      }}
    >
      <div
        class="container"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '18px 24px',
          gap: 16,
        }}
      >
        <a href="/" style={{ textDecoration: 'none', display: 'inline-flex' }}>
          <Wordmark size={28} />
        </a>
        <nav class="top-nav-links" aria-label="Sections">
          {NAV.map(([label, href]) => (
            <a key={href} href={hrefFor(href)}>
              {label}
            </a>
          ))}
        </nav>
        <nav style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <ThemeToggle />
          <a
            href="/auth"
            style={{
              fontFamily: 'var(--font-ui)',
              fontSize: 14,
              fontWeight: 600,
              color: 'var(--ink)',
              textDecoration: 'none',
              padding: '8px 14px',
              borderRadius: 999,
              background: 'var(--card)',
              boxShadow: 'var(--shadow-card)',
            }}
          >
            Sign in
          </a>
        </nav>
      </div>
    </header>
  );
}

export function Footer() {
  return (
    <footer style={{ padding: '32px 0 48px', borderTop: '1px solid var(--line-soft)' }}>
      <div class="container footer-row">
        <Wordmark size={22} />
        <nav style={{ display: 'flex', gap: 20, flexWrap: 'wrap', justifyContent: 'center' }}>
          {[
            ['Changelog', '/changelog'],
            ['Privacy', '/privacy'],
            ['Terms', '/terms'],
            ['Support', '/support'],
            ['Delete account', '/delete-account'],
          ].map(([label, href]) => (
            <a
              key={href}
              href={href}
              style={{
                fontFamily: 'var(--font-ui)',
                fontSize: 13,
                color: 'var(--ink-2)',
                textDecoration: 'none',
              }}
            >
              {label}
            </a>
          ))}
        </nav>
        <a
          class="footer-brand"
          href="https://insert-coin.io"
          target="_blank"
          rel="noopener"
          aria-label="Insert Coin"
        >
          <img src={icLogo} alt="Insert Coin" class="footer-ic-logo" />
        </a>
      </div>
    </footer>
  );
}

export function Eyebrow({ children }: { children: ComponentChildren }) {
  return (
    <div
      style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        color: 'var(--ember-deep)',
      }}
    >
      {children}
    </div>
  );
}

/** Scrolls to the URL's #fragment after the page mounts. The router resets
 *  the scroll to the top when it renders a route, so arriving at "/#pricing"
 *  from another page would otherwise land at the top. */
export function useScrollToHash() {
  useEffect(() => {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (!id) return;
    const t = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }, 60);
    return () => window.clearTimeout(t);
  }, []);
}

/** A small "NEW" / "IMPROVED" / "FIXED" chip. */
export function KindTag({ kind }: { kind: 'new' | 'improved' | 'fixed' }) {
  const label = kind === 'new' ? 'New' : kind === 'improved' ? 'Improved' : 'Fixed';
  return (
    <span class={`kind-tag kind-${kind}`}>{label}</span>
  );
}

// Real app screenshot inside a PhoneFrame, in the site's current theme when
// a dark capture exists (`/shots/<name>-dark.webp`). The OS status bar is
// cropped out of the source, so the frame's own notch sits cleanly over the
// app content; object-position: top keeps it anchored under the notch.
export function PhoneShot({
  name,
  alt,
  dark = true,
  eager,
}: {
  name: string;
  alt: string;
  /** Whether a dark variant was captured for this screen. */
  dark?: boolean;
  eager?: boolean;
}) {
  const theme = useSiteTheme();
  const src = `/shots/${name}${dark && theme === 'dark' ? '-dark' : ''}.webp`;
  return (
    <img
      src={src}
      alt={alt}
      loading={eager ? 'eager' : 'lazy'}
      width={620}
      height={1277}
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: 'top',
        display: 'block',
      }}
    />
  );
}

/** Store link for the visitor's platform — App Store on iOS, Play on
 *  Android, null on desktop (they get both badges instead). */
function platformStoreUrl(): string | null {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) return APP_STORE_URL;
  if (/Android/i.test(ua)) return PLAY_STORE_URL;
  return null;
}

/** Phones only: a pill pinned to the bottom that links to the right store,
 *  shown once the hero's own badges have scrolled away. */
export function GetAppBar() {
  const [url, setUrl] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    setUrl(platformStoreUrl());
    const onScroll = () => setVisible(window.scrollY > 560);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  if (!url) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      class={`get-app-bar${visible ? ' is-visible' : ''}`}
    >
      <span class="wordmark" style={{ fontSize: 20 }}>shotmeal</span>
      <span>Get the app — it's free</span>
    </a>
  );
}
